import React, { useState } from 'react';
import { AIContentGeneration } from '../types';
import { 
  LightBulbIcon, 
  ClockIcon, 
  TagIcon, 
  DocumentTextIcon,
  PlayIcon,
  PlusIcon,
  TrashIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';

interface AIContentGenerationProps {
  content: AIContentGeneration;
  onGenerateNewContent?: (type: 'quiz' | 'summary' | 'flashcards' | 'practice_exercises' | 'likely_questions') => void;
  onDeleteContent?: (contentId: string) => void;
}

const AIContentGenerationComponent: React.FC<AIContentGenerationProps> = ({
  content,
  onGenerateNewContent,
  onDeleteContent
}) => {
  const [activeTab, setActiveTab] = useState<'quiz' | 'summary' | 'flashcards' | 'practice_exercises' | 'likely_questions'>(content.type as any);
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<{ [key: number]: number }>({});
  const [quizResults, setQuizResults] = useState<{ score: number; feedback: string } | null>(null);

  const renderQuizContent = () => {
    if (content.type !== 'quiz') {
      return null;
    }
    
    const questions = content.content.questions || [];
    
    if (questions.length === 0) {
      return (
        <div className="text-center space-y-6 p-4 sm:p-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
            <DocumentTextIcon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
          </div>
          <div className="space-y-3">
            <h4 className="text-lg sm:text-xl font-bold text-gray-900">Quiz Content</h4>
            <p className="text-sm sm:text-base text-gray-600 max-w-md mx-auto px-4">
              No questions available yet. The AI might still be generating content.
            </p>
          </div>
          <button
            onClick={() => {
              if (onGenerateNewContent) {
                onGenerateNewContent('quiz');
              }
            }}
            className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center mx-auto text-sm sm:text-base font-semibold"
          >
            <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" />
            Regenerate Quiz
          </button>
        </div>
      );
    }

    if (showQuiz && !quizResults) {
      const currentQuestion = questions[currentQuestionIndex];
      
      return (
        <div className="space-y-4 sm:space-y-6">
          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 sm:h-3 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            ></div>
          </div>
          
          {/* Question header */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 sm:p-6 rounded-2xl border border-blue-100 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 mb-4">
              <span className="px-3 sm:px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-xs sm:text-sm font-medium self-start sm:self-auto">
                Question {currentQuestionIndex + 1} of {questions.length}
              </span>
              <span className="text-xs sm:text-sm text-gray-500">
                {Math.round(((currentQuestionIndex + 1) / questions.length) * 100)}% Complete
              </span>
            </div>
            <h4 className="text-lg sm:text-xl font-bold text-gray-900 leading-relaxed">
              {currentQuestion.question}
            </h4>
          </div>
          
          {/* Answer options */}
          <div className="space-y-3 sm:space-y-4">
            {currentQuestion.options?.map((option: string, index: number) => (
              <label 
                key={index} 
                className={`block cursor-pointer transition-all duration-200 ${
                  userAnswers[currentQuestionIndex] === index 
                    ? 'transform scale-105' 
                    : 'hover:scale-102'
                }`}
              >
                <input
                  type="radio"
                  name={`question-${currentQuestionIndex}`}
                  value={index}
                  checked={userAnswers[currentQuestionIndex] === index}
                  onChange={(e) => setUserAnswers({
                    ...userAnswers,
                    [currentQuestionIndex]: parseInt(e.target.value)
                  })}
                  className="sr-only"
                />
                <div className={`p-3 sm:p-4 rounded-xl border-2 transition-all duration-200 ${
                  userAnswers[currentQuestionIndex] === index
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-25'
                }`}>
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      userAnswers[currentQuestionIndex] === index
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300 bg-white'
                    }`}>
                      {userAnswers[currentQuestionIndex] === index && (
                        <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-white rounded-full"></div>
                      )}
                    </div>
                    <span className="text-gray-800 text-sm sm:text-base font-medium leading-relaxed">
                      {option}
                    </span>
                  </div>
                </div>
              </label>
            ))}
          </div>
          
          {/* Show explanation if user has answered */}
          {userAnswers[currentQuestionIndex] !== undefined && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 sm:p-6 rounded-2xl border border-green-200 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-3 mb-4">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  userAnswers[currentQuestionIndex] === currentQuestion.correctAnswer 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-red-100 text-red-600'
                }`}>
                  {userAnswers[currentQuestionIndex] === currentQuestion.correctAnswer ? (
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>
                <div className="text-center sm:text-left">
                  <h5 className="font-bold text-gray-900 text-base sm:text-lg">
                    {userAnswers[currentQuestionIndex] === currentQuestion.correctAnswer ? 'Correct!' : 'Incorrect'}
                  </h5>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {userAnswers[currentQuestionIndex] === currentQuestion.correctAnswer 
                      ? 'Great job! You got it right.' 
                      : 'Keep learning! Here\'s the explanation.'}
                  </p>
                </div>
              </div>
              
              <div className="bg-white p-3 sm:p-4 rounded-xl border border-green-200">
                <h6 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Explanation:</h6>
                <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                  {currentQuestion.explanation || 'This is the correct answer based on the lesson content.'}
                </p>
              </div>
            </div>
          )}
          
          {/* Navigation buttons */}
          <div className="flex flex-col sm:flex-row justify-between space-y-3 sm:space-y-0 sm:space-x-4 pt-4">
            <button
              onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
              disabled={currentQuestionIndex === 0}
              className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium flex items-center justify-center text-sm sm:text-base"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>
            
            {currentQuestionIndex < questions.length - 1 ? (
              <button
                onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                className="px-6 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all duration-200 font-semibold flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-105 text-sm sm:text-base"
              >
                Next
                <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <button
                onClick={() => {
                  const score = Object.keys(userAnswers).length > 0 ? 
                    Math.round((Object.values(userAnswers).filter((answer, index) => 
                      answer === questions[index].correctAnswer
                    ).length / questions.length) * 100) : 0;
                  
                  setQuizResults({
                    score,
                    feedback: score >= 80 ? 'Excellent work!' : score >= 60 ? 'Good effort!' : 'Keep practicing!'
                  });
                }}
                className="px-6 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-200 font-semibold flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-105 text-sm sm:text-base"
              >
                Submit Quiz
                <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}
          </div>
        </div>
      );
    }

    if (quizResults) {
      const getScoreColor = (score: number) => {
        if (score >= 80) return 'from-green-400 to-emerald-500';
        if (score >= 60) return 'from-yellow-400 to-orange-500';
        return 'from-red-400 to-pink-500';
      };
      
      const getScoreEmoji = (score: number) => {
        if (score >= 80) return '🎉';
        if (score >= 60) return '👍';
        return '💪';
      };
      
      return (
        <div className="text-center space-y-6 sm:space-y-8 p-4 sm:p-8 bg-gradient-to-br from-gray-50 to-white rounded-3xl border border-gray-100 shadow-lg">
          {/* Score Display */}
          <div className="space-y-4">
            <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center shadow-2xl">
              <div className="text-4xl sm:text-5xl">{getScoreEmoji(quizResults.score)}</div>
            </div>
            
            <div className="space-y-2">
              <div className={`text-5xl sm:text-6xl font-bold bg-gradient-to-r ${getScoreColor(quizResults.score)} bg-clip-text text-transparent`}>
                {quizResults.score}%
              </div>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{quizResults.feedback}</p>
            </div>
          </div>
          
          {/* Performance Summary */}
          <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-200 shadow-sm max-w-md mx-auto">
            <h4 className="font-bold text-gray-900 mb-4 text-base sm:text-lg">Performance Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-xl sm:text-2xl font-bold text-blue-600">{questions.length}</div>
                <div className="text-xs sm:text-sm text-gray-600">Total Questions</div>
              </div>
              <div>
                <div className="text-xl sm:text-2xl font-bold text-green-600">
                  {Object.values(userAnswers).filter((answer, index) => 
                    answer === questions[index].correctAnswer
                  ).length}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Correct Answers</div>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
            <button
              onClick={() => {
                setShowQuiz(false);
                setCurrentQuestionIndex(0);
                setUserAnswers({});
                setQuizResults(null);
              }}
              className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all duration-200 font-semibold flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-105 text-sm sm:text-base"
            >
              <PlayIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" />
              Take Quiz Again
            </button>
            <button
              onClick={() => {
                setShowQuiz(false);
                setCurrentQuestionIndex(0);
                setUserAnswers({});
                setQuizResults(null);
              }}
              className="px-6 sm:px-8 py-3 sm:py-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold flex items-center justify-center text-sm sm:text-base"
            >
              Close
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="text-center space-y-6 p-4 sm:p-8 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-100">
        <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
          <DocumentTextIcon className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
        </div>
        <div className="space-y-3">
          <h4 className="text-xl sm:text-2xl font-bold text-gray-900">AI-Generated Quiz</h4>
          <p className="text-base sm:text-lg text-gray-600 px-4">
            Test your knowledge with <span className="font-bold text-green-600">{content.content.questions?.length || 0}</span> questions
          </p>
        </div>
        
        <button
          onClick={() => setShowQuiz(true)}
          className="px-8 sm:px-10 py-3 sm:py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center mx-auto text-base sm:text-lg font-bold"
        >
          <PlayIcon className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" />
          Start Quiz
        </button>
      </div>
    );
  };

  const renderSummaryContent = () => {
    if (content.type !== 'summary') return null;
    
    const summaryData = content.content;
    
    return (
      <div className="space-y-6 sm:space-y-8">
        {/* Main Summary */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 sm:p-8 rounded-2xl border border-blue-100 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-4 sm:mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
              <DocumentTextIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <h4 className="text-xl sm:text-2xl font-bold text-gray-900 text-center sm:text-left">Summary</h4>
          </div>
          <p className="text-gray-700 text-base sm:text-lg leading-relaxed">{summaryData.summary}</p>
        </div>
        
        {/* Key Points and Terms Grid */}
        <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 sm:p-6 rounded-2xl border border-green-100 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <LightBulbIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <h5 className="text-lg sm:text-xl font-bold text-gray-900 text-center sm:text-left">Key Points</h5>
            </div>
            <ul className="space-y-2 sm:space-y-3">
              {summaryData.keyPoints?.map((point: string, index: number) => (
                <li key={index} className="flex items-start space-x-2 sm:space-x-3">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700 leading-relaxed text-sm sm:text-base">{point}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 sm:p-6 rounded-2xl border border-purple-100 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <TagIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <h5 className="text-lg sm:text-xl font-bold text-gray-900 text-center sm:text-left">Important Terms</h5>
            </div>
            <ul className="space-y-2 sm:space-y-3">
              {summaryData.importantTerms?.map((term: string, index: number) => (
                <li key={index} className="flex items-start space-x-2 sm:space-x-3">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700 leading-relaxed text-sm sm:text-base">{term}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderFlashcardsContent = () => {
    if (content.type !== 'flashcards') return null;
    
    const cards = content.content.cards || [];
    
    return (
      <div className="space-y-6 sm:space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
            <BookOpenIcon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
          </div>
          <h4 className="text-xl sm:text-2xl font-bold text-gray-900">AI-Generated Flashcards</h4>
          <p className="text-sm sm:text-base text-gray-600 px-4">Study and memorize key concepts</p>
        </div>
        
        {/* Flashcards Grid */}
        <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
          {cards.map((card: any, index: number) => (
            <div key={index} className="group perspective">
              <div className="relative w-full h-40 sm:h-48 transform-style-preserve-3d transition-transform duration-500 group-hover:rotate-y-180">
                {/* Front of card */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 p-4 sm:p-6 rounded-2xl border border-blue-200 shadow-sm flex items-center justify-center text-center backface-hidden">
                  <div className="space-y-2">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                      <span className="text-blue-600 font-bold text-xs sm:text-sm">{index + 1}</span>
                    </div>
                    <p className="text-sm sm:text-lg font-semibold text-gray-900 leading-relaxed">{card.front}</p>
                  </div>
                </div>
                
                {/* Back of card */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-emerald-50 p-4 sm:p-6 rounded-2xl border border-green-200 shadow-sm flex items-center justify-center text-center backface-hidden rotate-y-180">
                  <div className="space-y-2">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                      <span className="text-green-600 font-bold text-xs sm:text-sm">A</span>
                    </div>
                    <p className="text-sm sm:text-lg font-semibold text-gray-900 leading-relaxed">{card.back}</p>
                  </div>
                </div>
              </div>
              
              {/* Hover hint */}
              <div className="text-center mt-2 sm:mt-3">
                <span className="text-xs text-gray-500">Hover to flip</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPracticeExercisesContent = () => {
    if (content.type !== 'practice_exercises') return null;
    
    const exercises = content.content.exercises || [];
    
    return (
      <div className="space-y-6 sm:space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
            <LightBulbIcon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
          </div>
          <h4 className="text-xl sm:text-2xl font-bold text-gray-900">AI-Generated Practice Exercises</h4>
          <p className="text-sm sm:text-base text-gray-600 px-4">Apply your knowledge with hands-on practice</p>
        </div>
        
        {/* Exercises List */}
        <div className="space-y-4 sm:space-y-6">
          {exercises.map((exercise: any, index: number) => (
            <div key={index} className="bg-gradient-to-br from-orange-50 to-red-50 p-4 sm:p-6 rounded-2xl border border-orange-100 shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0 sm:space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0 self-center sm:self-auto">
                  <span className="text-white font-bold text-base sm:text-lg">{index + 1}</span>
                </div>
                <div className="flex-1 space-y-3 text-center sm:text-left">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                    <h5 className="text-lg sm:text-xl font-bold text-gray-900">
                      {exercise.type.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                    </h5>
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs sm:text-sm font-medium self-center sm:self-auto">
                      Practice
                    </span>
                  </div>
                  <p className="text-gray-700 text-base sm:text-lg leading-relaxed">{exercise.description}</p>
                  
                  {/* Exercise metadata */}
                  {exercise.difficulty && (
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 text-xs sm:text-sm text-gray-600">
                      <span className="flex items-center justify-center sm:justify-start space-x-1">
                        <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                        <span>Difficulty: {exercise.difficulty}</span>
                      </span>
                      {exercise.estimatedTime && (
                        <span className="flex items-center justify-center sm:justify-start space-x-1">
                          <ClockIcon className="w-4 h-4" />
                          <span>{exercise.estimatedTime} min</span>
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'quiz':
        return renderQuizContent();
      case 'summary':
        return renderSummaryContent();
      case 'flashcards':
        return renderFlashcardsContent();
      case 'practice_exercises':
        return renderPracticeExercisesContent();
      case 'likely_questions':
        // Display as quiz-like content if present
        return renderQuizContent();
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 sm:p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 text-center sm:text-left">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg self-center sm:self-auto">
              <LightBulbIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">AI-Generated Content</h3>
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 text-xs sm:text-sm text-gray-600">
                <span className="px-2 sm:px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium self-center sm:self-auto">
                  {content.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
                <span className="flex items-center justify-center sm:justify-start space-x-1">
                  <ClockIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>{content.metadata.estimatedTime} min estimated</span>
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-center sm:justify-end space-x-2 sm:space-x-3">
            {onGenerateNewContent && (
              <button
                onClick={() => onGenerateNewContent(content.type)}
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-200 font-medium flex items-center shadow-md hover:shadow-lg transform hover:scale-105"
              >
                <PlusIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Regenerate
              </button>
            )}
            {onDeleteContent && (
              <button
                onClick={() => onDeleteContent(content.id)}
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl hover:from-red-600 hover:to-pink-600 transition-all duration-200 font-medium flex items-center shadow-md hover:shadow-lg transform hover:scale-105"
              >
                <TrashIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content Type Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-1 p-4">
          {(['quiz', 'summary', 'flashcards', 'practice_exercises', 'likely_questions'] as const).map((type) => (
            <button
              key={type}
              onClick={() => setActiveTab(type)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === type
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {renderContent()}
      </div>

      {/* Metadata Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center space-x-4">
            <span className="flex items-center">
              <ClockIcon className="w-4 h-4 mr-1" />
              {content.metadata.estimatedTime} min
            </span>
            <span className="flex items-center">
              <TagIcon className="w-4 h-4 mr-1" />
              {content.metadata.difficulty}
            </span>
          </div>
          <span className="text-xs">Generated by {content.metadata.aiModel}</span>
        </div>
      </div>
    </div>
  );
};

export default AIContentGenerationComponent;
